import { useTranslation } from 'react-i18next'
import { Box, ResponsiveRender, Text } from '@machobear/design-system'
import { useEffect, useState } from 'react'
import * as styles from './sidebar.css'

const SidebarLink = ({
  active,
  handleClick,
  children
}: {
  active: boolean
  handleClick: () => void
  children: React.ReactNode
}) => {
  return (
    <Box
      borderLeft={active ? 'borderField' : 'borderNeutralLight'}
      style={{
        borderLeftWidth: active ? '2px' : '1px'
      }}
      cursor="pointer"
      onClick={handleClick}
      role="button"
      tabIndex={0}
      paddingLeft="xxsmall"
    >
      <Box
        width="full"
        height="full"
        paddingY="xsmall"
        paddingX="small"
        borderRadius="small"
        style={{
          paddingLeft: active ? '15px' : '16px'
        }}
        className={styles.sidebarLink}
      >
        <Text weight="strong" baseline={false}>
          {children}
        </Text>
      </Box>
    </Box>
  )
}

const SidebarLinks = ({
  isLoading,
  refs
}: {
  isLoading: boolean
  refs: Record<string, React.RefObject<HTMLDivElement>>
}) => {
  const { t } = useTranslation()
  const [activeSection, setActiveSection] = useState<keyof typeof refs>('information')

  useEffect(() => {
    const observerCallback = (entries: IntersectionObserverEntry[]) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const visibleSection = Object.keys(refs).find(key => refs[key]?.current === entry.target)
          if (visibleSection) {
            setActiveSection(visibleSection)
          }
        }
      })
    }

    const observer = new IntersectionObserver(observerCallback, {
      root: null,
      rootMargin: '-50% 0%',
      threshold: 0
    })

    Object.values(refs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current)
      }
    })

    return () => {
      observer.disconnect()
    }
  }, [refs])

  const handleScroll = (section: string) => {
    const element = refs[section]?.current
    if (element) {
      const offset = 0
      const elementPosition = element.offsetTop
      const offsetPosition = elementPosition - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
    }
  }

  const links = [
    { key: 'information', label: t('Listing Information', 'Listing Information') },
    { key: 'terms', label: t('Lease Terms', 'Lease Terms') },
    { key: 'features', label: t('Features & Utilities', 'Features & Utilities') },
    { key: 'photo', label: t('Photos', 'Photos') },
    { key: 'video', label: t('Videos', 'Videos') },
    { key: 'tour', label: t('3D Tour', '3D Tour') },
    { key: 'landlords', label: t('Manage Landlords', 'Manage Landlords') }
  ]

  return (
    <ResponsiveRender above="tablet">
      <Box display="flex" flexDirection="column" gap="none">
        {links.map(({ key, label }) => (
          <SidebarLink
            key={key}
            active={!isLoading && activeSection === key}
            handleClick={() => handleScroll(key)}
          >
            {label}
          </SidebarLink>
        ))}
      </Box>
    </ResponsiveRender>
  )
}

export default SidebarLinks
